import React from "react";
import { Table, Header, TableProps } from "@amzn/awsui-components-console";
import { FixedFeeConfig } from "@amzn/fuse-accounting-central-datastore-client";
import {
  ARRAY_CELL_WRAPPER,
  BADGE_CELL_WRAPPER,
  getFixedFeeDealDateStatus,
} from "../allFixedFeeConfigsPage/fixed-fee-config-attributes";

interface Props {
  fixedFeeConfig: FixedFeeConfig;
}

const DUMMY_COLUMN_DEFINITION = {
  id: "",
  header: "",
  cell: () => "",
};

// Helper function to format numeric values
const formatNumber = (value: any) => {
  if (!value) return "-";
  return Number(value.string).toLocaleString("en-US", {
    maximumFractionDigits: 4,
  });
};

export const TopLevelTable: React.FC<Props> = ({ fixedFeeConfig }) => {
  const topLevelColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    DUMMY_COLUMN_DEFINITION,
    {
      id: "fixedFeeId",
      header: "Fixed Fee ID",
      cell: (item: any) => item.fixedFeeId || "-",
    },
    {
      id: "environment",
      header: "Environment",
      cell: (item: any) => item.environment || "-",
    },
    {
      id: "dealStatus",
      header: "Deal Status",
      cell: (item: any) => item.dealStatus || "-",
    },
    {
      id: "dealDateStatus",
      header: "Deal Date Status",
      cell: (item: any) =>
        BADGE_CELL_WRAPPER(
          getFixedFeeDealDateStatus(item.dealStartDate!, item.dealEndDate!),
        ),
    },
    {
      id: "dealStartDate",
      header: "Deal Start Date",
      cell: (item: any) => item.dealStartDate || "-",
      minWidth: 120,
    },
    {
      id: "dealEndDate",
      header: "Deal End Date",
      cell: (item: any) => item.dealEndDate || "-",
      minWidth: 120,
    },
    {
      id: "resellerIds",
      header: "Reseller IDs",
      cell: (item: any) =>
        item.dealConfig?.resellerIds
          ? ARRAY_CELL_WRAPPER(item.dealConfig?.resellerIds)
          : "-",
      maxWidth: 200,
    },
    {
      id: "fusePlanIds",
      header: "Fuse Plan IDs",
      cell: (item: any) =>
        item.dealConfig?.fusePlanIds
          ? ARRAY_CELL_WRAPPER(item.dealConfig?.fusePlanIds)
          : "-",
      minWidth: 380,
      maxWidth: 400,
    },
    {
      id: "serviceProvider",
      header: "Service Provider",
      cell: (item: any) => item.serviceProvider || "-",
    },
    {
      id: "contractPrice",
      header: "Contract Price",
      cell: (item: any) => formatNumber(item.contractPrice),
    },
    {
      id: "initialPlanPrice",
      header: "Initial Plan Price",
      cell: (item: any) => formatNumber(item.initialPlanPrice),
    },
    {
      id: "currencyCode",
      header: "Currency Code",
      cell: (item: any) => item.contractCurrencyCode || "-",
    },
    {
      id: "adjustmentFrequency",
      header: "Adjustment Frequency",
      cell: (item: any) => item.adjustmentFrequency || "-",
    },
    {
      id: "invoiceFrequency",
      header: "Invoice Frequency",
      cell: (item: any) => item.invoiceFrequency || "-",
    },
    {
      id: "marketplaceId",
      header: "Marketplace ID",
      cell: (item: any) => item.dealConfig?.marketplaceId || "-",
    },
    {
      id: "accountingRegistrationId",
      header: "Accounting Registration ID",
      cell: (item: any) => item.accountingRegistrationId || "-",
    },
    DUMMY_COLUMN_DEFINITION,
  ];

  const topLevelData = [fixedFeeConfig];

  return (
    <Table
      columnDefinitions={topLevelColumnDefinitions}
      items={topLevelData}
      wrapLines={true}
      resizableColumns={false}
      header={<Header>Top-Level Information</Header>}
    />
  );
};
